import React, { useState, useEffect } from 'react';
import { Modal, Typography, Button } from 'antd';
import {
    CalendarOutlined,
    AppstoreOutlined,
    FileTextOutlined,
    ArrowRightOutlined,
    UserOutlined,
    LinkOutlined,
    TagOutlined,
    ClockCircleOutlined,
    InboxOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

export default function NewProjectRequestModal({ open, request, requests = [], onClose, onReviewRequest }) {
    const requestList = requests.length > 0 ? requests : (request ? [request] : []);
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (request && requestList.length > 0) {
            const idx = requestList.findIndex((r) => r._id === request._id || r.pcrNumber === request.pcrNumber);
            if (idx >= 0) setSelectedIndex(idx);
        }
    }, [request, requestList]);

    if (!open || requestList.length === 0) return null;

    const currentReq = requestList[selectedIndex] || requestList[0];

    const handleReviewClick = () => {
        if (onReviewRequest) {
            onReviewRequest(currentReq);
        }
        onClose();
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={900}
            centered
            className="p-0 overflow-hidden rounded-2xl"
            closeIcon={
                <span className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center font-bold text-sm transition-colors">
                    ✕
                </span>
            }
        >
            <div className="flex h-[620px] bg-white font-sans overflow-hidden rounded-2xl">
                
                {/* ── LEFT SIDEBAR (Pending Submissions Master List) ───────────── */}
                <div className="w-[290px] bg-slate-50 border-r border-slate-200 flex flex-col h-full shrink-0">
                    
                    {/* Sidebar Header */}
                    <div className="p-4 border-b border-slate-200 bg-slate-100/60 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <InboxOutlined className="text-[#00AAF1] text-base" />
                            <span className="text-xs font-black uppercase tracking-wider text-slate-700">
                                Pending Submissions
                            </span>
                        </div>
                        <span className="bg-[#00AAF1] text-white font-black text-[11px] px-2 py-0.5 rounded-full shadow-xs">
                            {requestList.length}
                        </span>
                    </div>

                    {/* Scrollable Submissions List */}
                    <div className="flex-1 overflow-y-auto p-3 space-y-2 scrollbar-thin">
                        {requestList.map((req, idx) => {
                            const active = idx === selectedIndex;
                            const createdDate = req.createdAt ? dayjs(req.createdAt).format('DD MMM') : null;

                            return (
                                <div
                                    key={req._id || req.pcrNumber || idx}
                                    onClick={() => setSelectedIndex(idx)}
                                    className={`p-3 rounded-xl cursor-pointer transition-all border ${
                                        active
                                            ? 'bg-white border-l-4 border-l-[#00AAF1] border-t-slate-200 border-r-slate-200 border-b-slate-200 shadow-sm'
                                            : 'bg-white/60 border-slate-200/80 hover:bg-white hover:border-slate-300 text-slate-600'
                                    }`}
                                >
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className={`font-mono text-[11px] font-bold px-2 py-0.5 rounded ${
                                            active ? 'bg-sky-50 text-[#00AAF1]' : 'bg-slate-100 text-slate-500'
                                        }`}>
                                            {req.pcrNumber || `PCR-00${idx + 1}`}
                                        </span>
                                        {createdDate && (
                                            <span className="text-[10px] text-slate-400 font-medium">
                                                {createdDate}
                                            </span>
                                        )}
                                    </div>

                                    <h4 className={`text-xs font-bold leading-snug mb-1 line-clamp-2 ${
                                        active ? 'text-slate-900' : 'text-slate-700'
                                    }`}>
                                        {req.name || 'Untitled Request'}
                                    </h4>

                                    <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1 border-t border-slate-100">
                                        <span className="truncate max-w-[130px] font-medium text-slate-500">
                                            {req.owner?.name || req.owner?.email || 'BD Intake'}
                                        </span>
                                        <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.2 rounded">
                                            Pending
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Sidebar Footer Hint */}
                    <div className="p-3 bg-slate-100/70 border-t border-slate-200 text-center text-[11px] text-slate-500 font-medium">
                        Select a request to view details
                    </div>
                </div>

                {/* ── RIGHT DETAIL PANE ────────────────────────────────────────── */}
                <div className="flex-1 flex flex-col h-full bg-white overflow-hidden">
                    
                    {/* Header */}
                    <div className="p-6 border-b border-slate-150 bg-white">
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2">
                                <span className="bg-sky-50 text-[#00AAF1] border border-sky-200 font-extrabold text-[11px] px-2.5 py-0.5 rounded-md uppercase tracking-wider">
                                    {currentReq.source || 'CRE Intake'}
                                </span>
                                <span className="bg-amber-50 text-amber-700 border border-amber-200 font-extrabold text-[11px] px-2.5 py-0.5 rounded-md">
                                    {currentReq.status || 'Pending Review'}
                                </span>
                                <span className="bg-cyan-50 text-cyan-800 border border-cyan-200 font-extrabold text-[11px] px-2.5 py-0.5 rounded-md">
                                    {currentReq.brandName || (currentReq.brandId?.includes('e360') ? 'e360Digital' : currentReq.brandId?.includes('cport') ? 'CellularPort' : 'DymaxTech')}
                                </span>
                            </div>
                            <span className="font-mono text-xs font-bold text-slate-600 bg-slate-100 px-3 py-1 rounded-lg border border-slate-200">
                                {currentReq.pcrNumber || 'PCR-0001'}
                            </span>
                        </div>

                        <Title level={3} className="!text-slate-900 !mb-1 font-extrabold tracking-tight">
                            {currentReq.name || 'New Client Project Request'}
                        </Title>

                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                            <div className="flex items-center gap-1.5">
                                <UserOutlined className="text-slate-400" />
                                <span>Submitted by <strong className="text-slate-700">{currentReq.owner?.name || currentReq.owner?.email || 'BD Team'}</strong></span>
                            </div>
                            <span>•</span>
                            <div className="flex items-center gap-1.5 font-mono">
                                <TagOutlined className="text-slate-400" />
                                <span>CRE Ref ID: <strong className="text-slate-700">{currentReq.creProjectId || 'CRE-001'}</strong></span>
                            </div>
                        </div>
                    </div>

                    {/* Right Scrollable Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/40 scrollbar-thin">
                        
                        {/* Scope Card */}
                        <div className="bg-white p-4 rounded-xl border border-slate-200/80 border-l-4 border-l-[#00AAF1] shadow-xs space-y-1">
                            <Text className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wider block">
                                Project Overview & Scope
                            </Text>
                            <Paragraph className="text-slate-700 text-sm !mb-0 font-normal leading-relaxed">
                                {currentReq.description || 'No detailed scope description provided.'}
                            </Paragraph>
                        </div>

                        {/* 3-Column Specs Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs">
                                <div className="flex items-center gap-2 text-slate-400 mb-1">
                                    <AppstoreOutlined className="text-[#00AAF1]" />
                                    <Text className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400">Classification</Text>
                                </div>
                                <Text className="font-semibold text-slate-800 text-sm block">
                                    {currentReq.category || 'Website'} / {currentReq.subCategory || currentReq.subcategory || 'Development'}
                                </Text>
                            </div>

                            <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs">
                                <div className="flex items-center gap-2 text-slate-400 mb-1">
                                    <CalendarOutlined className="text-[#00AAF1]" />
                                    <Text className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400">Target ETA</Text>
                                </div>
                                <Text className="font-semibold text-slate-800 text-sm block">
                                    {currentReq.eta ? dayjs(currentReq.eta).format('DD MMM YYYY') : 'Flexible'}
                                </Text>
                            </div>

                            <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs">
                                <div className="flex items-center gap-2 text-slate-400 mb-1">
                                    <TagOutlined className="text-[#00AAF1]" />
                                    <Text className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400">Brand</Text>
                                </div>
                                <Text className="font-semibold text-slate-800 text-sm block">
                                    {currentReq.brandName || (currentReq.brandId?.includes('e360') ? 'e360Digital' : currentReq.brandId?.includes('cport') ? 'CellularPort' : 'DymaxTech')}
                                </Text>
                            </div>
                        </div>

                        {/* Intake Questionnaire Banner */}
                        <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-sky-50 text-[#00AAF1] flex items-center justify-center text-lg font-bold border border-sky-100">
                                    <FileTextOutlined />
                                </div>
                                <div>
                                    <Text className="font-bold text-slate-800 block text-sm">
                                        {currentReq.questionnaire?.name || 'Client Intake Questionnaire'}
                                    </Text>
                                    <Text className="text-xs text-slate-400">
                                        {currentReq.milestones?.length || 0} High-Level Milestones Attached
                                    </Text>
                                </div>
                            </div>
                            {currentReq.questionnaire?.url && (
                                <a
                                    href={currentReq.questionnaire.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs font-bold text-[#00AAF1] hover:underline flex items-center gap-1.5 bg-sky-50 hover:bg-sky-100 px-3.5 py-1.5 rounded-lg border border-sky-200 transition-colors"
                                >
                                    View Questionnaire <LinkOutlined />
                                </a>
                            )}
                        </div>

                        {/* CRE High Level Milestones */}
                        {currentReq.milestones && currentReq.milestones.length > 0 && (
                            <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs space-y-2.5">
                                <div className="flex items-center justify-between">
                                    <Text className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wider block">
                                        High-Level Milestones
                                    </Text>
                                    <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
                                        {currentReq.milestones.length} Phases
                                    </span>
                                </div>
                                <div className="space-y-2">
                                    {currentReq.milestones.map((m, idx) => (
                                        <div key={m._id || idx} className="flex items-center justify-between text-xs bg-slate-50 p-2.5 rounded-lg border border-slate-200/60">
                                            <div className="flex items-center gap-2.5">
                                                <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold text-[10px]">
                                                    ✓
                                                </div>
                                                <span className="font-semibold text-slate-700 text-sm">{m.title || m.name}</span>
                                                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200">
                                                    {m.status || 'Pending'}
                                                </span>
                                            </div>
                                            <span className="text-slate-500 font-mono text-xs bg-white px-2 py-0.5 rounded border border-slate-200">
                                                ETA: {m.eta ? dayjs(m.eta).format('DD MMM YYYY') : 'TBD'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Intake Requirement Checklist */}
                        {currentReq.requirementChecklist && currentReq.requirementChecklist.length > 0 && (
                            <div className="bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs space-y-2.5">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Text className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wider block">
                                            Intake Requirements Checklist
                                        </Text>
                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-50 text-[#00AAF1] border border-sky-200">
                                            {currentReq.requirementChecklist.length} Items
                                        </span>
                                    </div>
                                    {currentReq.requirementChecklist.some(r => r.status === 'Resubmitted') && (
                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-300 animate-pulse">
                                            🔄 Resubmissions Awaiting Review
                                        </span>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    {currentReq.requirementChecklist.map((item, idx) => {
                                        const isResubmitted = item.status === 'Resubmitted';
                                        const isApproved = item.status === 'Approved' || item.met;
                                        const isRejected = item.status === 'Rejected' || item.rejected;
                                        return (
                                            <div
                                                key={item.checklistItemId || idx}
                                                className={`p-3 rounded-xl border text-xs transition-all ${
                                                    isResubmitted
                                                        ? 'bg-amber-50/70 border-amber-200 shadow-2xs'
                                                        : isApproved
                                                        ? 'bg-emerald-50/40 border-emerald-200/60'
                                                        : isRejected
                                                        ? 'bg-rose-50/40 border-rose-200/60'
                                                        : 'bg-slate-50 border-slate-200/60'
                                                }`}
                                            >
                                                <div className="flex items-center justify-between gap-2 mb-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`w-2 h-2 rounded-full ${
                                                            isResubmitted ? 'bg-amber-500' : isApproved ? 'bg-emerald-500' : isRejected ? 'bg-rose-500' : 'bg-slate-400'
                                                        }`} />
                                                        <span className="font-bold text-slate-800 text-sm">
                                                            {item.label}
                                                        </span>
                                                    </div>
                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase tracking-wider ${
                                                        isResubmitted
                                                            ? 'bg-amber-100 text-amber-800 border-amber-300'
                                                            : isApproved
                                                            ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                                            : isRejected
                                                            ? 'bg-rose-100 text-rose-800 border-rose-300'
                                                            : 'bg-slate-100 text-slate-600 border-slate-200'
                                                    }`}>
                                                        {item.status || (isApproved ? 'Approved' : 'Pending')}
                                                    </span>
                                                </div>
                                                {item.detail && (
                                                    <p className="text-slate-500 text-xs mb-1 pl-4">
                                                        {item.detail}
                                                    </p>
                                                )}
                                                {item.fixNote && (
                                                    <div className="mt-1.5 ml-4 p-2 bg-white/95 rounded-lg border border-amber-200 text-xs text-amber-900 flex items-start gap-1.5 shadow-2xs">
                                                        <span className="font-bold shrink-0">Fix Note:</span>
                                                        <span className="italic font-medium">"{item.fixNote}"</span>
                                                    </div>
                                                )}
                                                {item.rejectionNote && !item.fixNote && (
                                                    <div className="mt-1.5 ml-4 p-2 bg-white/95 rounded-lg border border-rose-200 text-xs text-rose-900 flex items-start gap-1.5 shadow-2xs">
                                                        <span className="font-bold shrink-0">Previous Rejection:</span>
                                                        <span className="italic font-medium">"{item.rejectionNote}"</span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Fixed Footer Bar */}
                    <div className="p-4 bg-white border-t border-slate-150 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                            <ClockCircleOutlined className="text-amber-500" />
                            <span>Awaiting PM Review</span>
                        </div>

                        <div className="flex items-center gap-3">
                            <Button
                                onClick={onClose}
                                className="rounded-xl px-5 font-semibold text-slate-600 border-slate-200 hover:bg-slate-50"
                            >
                                Dismiss
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleReviewClick}
                                className="rounded-xl px-6 py-5 font-extrabold bg-gradient-to-r from-[#03488A] to-[#00AAF1] border-0 hover:opacity-95 shadow-md flex items-center gap-2 text-sm"
                            >
                                Review & Approve Project <ArrowRightOutlined />
                            </Button>
                        </div>
                    </div>
                </div>

            </div>
        </Modal>
    );
}
